import { useEffect } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import HomePage from './pages/HomePage'
import StudyPage from './pages/StudyPage'
import ReviewPage from './pages/ReviewPage'
import OraclePage from './pages/OraclePage'
import MistakeBookPage from './pages/MistakeBookPage'
import TeahousePage from './pages/TeahousePage'
import AchievementToast from './components/Common/AchievementToast'
import PageLoader from './components/Common/PageLoader'
import { useGameStore } from './store/gameStore'
import { playClickSound, playPageTransitionSound } from './utils/soundEngine'

function App() {
  const settings = useGameStore(s => s.settings)
  const location = useLocation()

  // 把设置落到 <html> 的类上：动画档位门控所有闪烁（默认安静），字号档位缩放 rem 基准
  useEffect(() => {
    const root = document.documentElement
    root.classList.toggle('anim-slow', settings.animationSpeed === 'normal')
    root.classList.toggle('anim-fast', settings.animationSpeed === 'fast')
    root.classList.toggle('text-scale-small', settings.fontSize === 'small')
    root.classList.toggle('text-scale-large', settings.fontSize === 'large')
  }, [settings])

  // 页面切换时播放像素音效
  useEffect(() => {
    playPageTransitionSound()
  }, [location.pathname, location.search])

  // 全局按钮与交互元素点击像素音效及 .pixel-button 点击像素涟漪触发代理
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null
      if (!target) return
      const interactiveEl = target.closest(
        '.pixel-button, button, a, [role="button"], input[type="button"], input[type="submit"]'
      )
      if (interactiveEl) {
        playClickSound()
      }

      // 当用户点击 .pixel-button 时，在点击位置生成像素点扩散动画效果（涟漪效果）并触发震动反馈
      const pixelBtn = target.closest('.pixel-button')
      if (pixelBtn) {
        if (typeof window !== 'undefined' && 'navigator' in window && typeof window.navigator.vibrate === 'function') {
          try {
            window.navigator.vibrate(20)
          } catch {
            // 忽略某些浏览器触感反馈受限异常
          }
        }

        const rect = pixelBtn.getBoundingClientRect()
        const centerX = rect.left + rect.width / 2
        const centerY = rect.top + rect.height / 2
        
        // 计算点击点相对于按钮中心的偏移比例 (-1 到 1)，转换为微观像素平移 (-8px 到 8px)
        const rawOffsetX = rect.width > 0 ? ((e.clientX - centerX) / (rect.width / 2)) * 8 : 0
        const rawOffsetY = rect.height > 0 ? ((e.clientY - centerY) / (rect.height / 2)) * 8 : 0
        
        const offsetX = Math.max(-12, Math.min(12, Math.round(rawOffsetX)))
        const offsetY = Math.max(-12, Math.min(12, Math.round(rawOffsetY)))

        const ripple = document.createElement('div')
        ripple.className = 'pixel-ripple-effect'
        ripple.style.left = `${e.clientX}px`
        ripple.style.top = `${e.clientY}px`
        ripple.style.setProperty('--ripple-offset-x', `${offsetX}px`)
        ripple.style.setProperty('--ripple-offset-y', `${offsetY}px`)
        document.body.appendChild(ripple)

        setTimeout(() => {
          ripple.remove()
        }, 350)

        // 喷射像素爆炸粒子 (pixel-burst-particle)，增强全站按钮击打反馈
        const particleColors = [
          'var(--color-gold)',
          'var(--color-bull)',
          'var(--color-cyan)',
          '#ffffff',
        ]
        const particleCount = 6
        for (let i = 0; i < particleCount; i++) {
          const angle = (i / particleCount) * Math.PI * 2 + (Math.random() * 0.4 - 0.2)
          const distance = Math.floor(Math.random() * 28) + 14
          const tx = Math.round(Math.cos(angle) * distance)
          const ty = Math.round(Math.sin(angle) * distance)
          const size = Math.floor(Math.random() * 3) + 4
          const color = particleColors[Math.floor(Math.random() * particleColors.length)] || 'var(--color-gold)'

          const p = document.createElement('div')
          p.className = 'pixel-burst-particle'
          p.style.position = 'fixed'
          p.style.left = `${e.clientX - size / 2}px`
          p.style.top = `${e.clientY - size / 2}px`
          p.style.width = `${size}px`
          p.style.height = `${size}px`
          p.style.backgroundColor = color
          p.style.setProperty('--tw-particle-x', `${tx}px`)
          p.style.setProperty('--tw-particle-y', `${ty}px`)
          document.body.appendChild(p)

          setTimeout(() => {
            p.remove()
          }, 600)
        }
      }
    }

    window.addEventListener('click', handleClick, true)
    return () => window.removeEventListener('click', handleClick, true)
  }, [])

  return (
    <>
      {/* 全局像素风 Loading 动画，页面切换时自动触发 */}
      <PageLoader />

      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/study/:chapterId/:sectionId" element={<StudyPage />} />
        <Route path="/review" element={<ReviewPage />} />
        <Route path="/oracle" element={<OraclePage />} />
        <Route path="/mistakes" element={<MistakeBookPage />} />
        <Route path="/teahouse" element={<TeahousePage />} />
      </Routes>
      {/* 全局勋章解锁 Toast - 答完题触发, 4 秒自动消失 */}
      <AchievementToast />
    </>
  )
}

export default App
